import type {
    ExternalAtoms,
    RowData,
    TableOptions,
    TableState,
} from "@tanstack/react-table";
import type { DataGridColumn, DataGridTable } from "./columns/types";
import { dataGridFeatures, type DataGridFeatures } from "./features";
import { getDataTableSearch } from "./features/search";
import { getInitialColumnPinning } from "./layout/pinning";
import type { DataGridRootState, UseDataGridOptions } from "./types";

type Options<TData extends RowData> = UseDataGridOptions<TData>;

/** Root selector: only slices that change which rows or columns exist. */
export const selectRootState = (
    state: TableState<DataGridFeatures>,
): DataGridRootState => ({
    sorting: state.sorting,
    columnFilters: state.columnFilters,
    globalFilter: state.globalFilter,
    grouping: state.grouping,
    expanded: state.expanded,
    pagination: state.pagination,
    columnOrder: state.columnOrder,
    columnVisibility: state.columnVisibility,
    columnPinning: state.columnPinning,
});

/** Controlled slices (`state` + `onChange`); omitted keys stay internal. */
export const buildControlledState = (
    states: Partial<TableState<DataGridFeatures>>,
): Partial<TableState<DataGridFeatures>> => ({
    ...(states.rowSelection && { rowSelection: states.rowSelection }),
    ...(states.sorting && { sorting: states.sorting }),
    ...(states.globalFilter !== undefined && {
        globalFilter: states.globalFilter,
    }),
    ...(states.columnFilters && { columnFilters: states.columnFilters }),
    ...(states.expanded !== undefined && { expanded: states.expanded }),
    ...(states.grouping && { grouping: states.grouping }),
    ...(states.pagination && { pagination: states.pagination }),
});

/**
 * Change handlers for controlled slices. Keys must be omitted, not set to
 * `undefined`: an `undefined` handler would replace the feature's internal
 * state updater.
 */
export const buildChangeHandlers = <TData extends RowData>(
    handlers: Pick<
        TableOptions<DataGridFeatures, TData>,
        | "onRowSelectionChange"
        | "onSortingChange"
        | "onGlobalFilterChange"
        | "onColumnFiltersChange"
        | "onExpandedChange"
        | "onGroupingChange"
        | "onPaginationChange"
    >,
): Partial<TableOptions<DataGridFeatures, TData>> => ({
    ...(handlers.onRowSelectionChange && {
        onRowSelectionChange: handlers.onRowSelectionChange,
    }),
    ...(handlers.onSortingChange && {
        onSortingChange: handlers.onSortingChange,
    }),
    ...(handlers.onGlobalFilterChange && {
        onGlobalFilterChange: handlers.onGlobalFilterChange,
    }),
    ...(handlers.onColumnFiltersChange && {
        onColumnFiltersChange: handlers.onColumnFiltersChange,
    }),
    ...(handlers.onExpandedChange && {
        onExpandedChange: handlers.onExpandedChange,
    }),
    ...(handlers.onGroupingChange && {
        onGroupingChange: handlers.onGroupingChange,
    }),
    ...(handlers.onPaginationChange && {
        onPaginationChange: handlers.onPaginationChange,
    }),
});

/** Caller-owned atoms; read once, identities must stay stable. */
export const buildExternalAtoms = <TData extends RowData>(
    options: Options<TData>,
): ExternalAtoms<DataGridFeatures> => ({
    ...(options.rowSelection?.atom && {
        rowSelection: options.rowSelection.atom,
    }),
    ...(options.sorting?.atom && { sorting: options.sorting.atom }),
    ...(options.globalFilter?.atom && {
        globalFilter: options.globalFilter.atom,
    }),
    ...(options.columnFilters?.atom && {
        columnFilters: options.columnFilters.atom,
    }),
    ...(options.expanding?.atom && { expanded: options.expanding.atom }),
    ...(options.grouping?.atom && { grouping: options.grouping.atom }),
    ...(options.pagination?.atom && { pagination: options.pagination.atom }),
});

/**
 * Mount-time state: slice `initial` values, column state (or the default
 * column state when none is given), and meta pins.
 */
export const buildInitialState = <TData extends RowData>(
    options: Options<TData>,
): Partial<TableState<DataGridFeatures>> => {
    const columnState = options.columnState ?? options.defaultColumnState;
    return {
        ...(options.rowSelection?.initial && {
            rowSelection: options.rowSelection.initial,
        }),
        ...(options.sorting?.initial && { sorting: options.sorting.initial }),
        ...(options.globalFilter?.initial !== undefined && {
            globalFilter: options.globalFilter.initial,
        }),
        ...(options.columnFilters?.initial && {
            columnFilters: options.columnFilters.initial,
        }),
        ...(options.expanding?.initial !== undefined && {
            expanded: options.expanding.initial,
        }),
        ...(options.grouping?.initial && {
            grouping: options.grouping.initial,
        }),
        ...(options.pagination?.initial && {
            pagination: options.pagination.initial,
        }),
        columnOrder: columnState?.order ?? [],
        columnSizing: columnState?.sizing ?? {},
        columnVisibility: columnState?.visibility ?? {},
        columnPinning:
            columnState?.pinning ?? getInitialColumnPinning(options.columns),
    };
};

interface BuildTableOptionsInput<TData extends RowData> {
    data: readonly TData[];
    columns: ReadonlyArray<DataGridColumn<TData>>;
    getRowId: (row: TData, index: number) => string;
    getSubRows: ((row: TData) => readonly TData[] | undefined) | undefined;
    isRowSelectable: ((row: TData) => boolean) | undefined;
    controlledState: Partial<TableState<DataGridFeatures>>;
    changeHandlers: Partial<TableOptions<DataGridFeatures, TData>>;
    externalAtoms: ExternalAtoms<DataGridFeatures>;
    initialState: Partial<TableState<DataGridFeatures>>;
    capabilities: DataGridCapabilities;
    serverRowCount: number | undefined;
    serverPageCount: number | undefined;
    meta: Options<TData>["meta"];
}

/** Which capabilities are on; fixed for the table's lifetime. */
export interface DataGridCapabilities {
    hasRowSelection: boolean;
    isServerSorting: boolean;
    hasGlobalFilter: boolean;
    isServerFiltering: boolean;
    hasExpanding: boolean;
    hasParentSummaries: boolean;
    hasGrouping: boolean;
    hasClientPagination: boolean;
    isServerPagination: boolean;
    hasRangeSelection: boolean;
    isSubRowSelectionCascading: boolean;
    hasSeparatorRows: boolean;
    hasRowOpen: boolean;
}

export const getCapabilities = <TData extends RowData>(
    options: Options<TData>,
): DataGridCapabilities => ({
    hasRowSelection: Boolean(options.rowSelection),
    isServerSorting: options.sorting?.mode === "server",
    hasGlobalFilter: Boolean(options.globalFilter),
    isServerFiltering:
        options.globalFilter?.mode === "server" ||
        options.columnFilters?.mode === "server",
    hasExpanding: Boolean(options.expanding),
    hasParentSummaries: options.expanding?.parentRows === "summary",
    hasGrouping: Boolean(options.grouping),
    hasClientPagination: options.pagination?.mode === "client",
    isServerPagination: options.pagination?.mode === "server",
    hasRangeSelection: Boolean(options.rangeSelection),
    isSubRowSelectionCascading: options.rowSelection?.subRows !== "parentOnly",
    hasSeparatorRows: Boolean(options.separatorRows),
    hasRowOpen: Boolean(options.onRowOpen),
});

/** Server row count for manual pagination; it changes with every query. */
export const getServerRowCount = <TData extends RowData>(
    options: Options<TData>,
): number | undefined =>
    options.pagination?.mode === "server"
        ? options.pagination.rowCount
        : undefined;

/** Server page count when the server pages by something other than rows. */
export const getServerPageCount = <TData extends RowData>(
    options: Options<TData>,
): number | undefined =>
    options.pagination?.mode === "server"
        ? options.pagination.pageCount
        : undefined;

/**
 * Rows before the current page. Zero when the server gives only a page
 * count, since earlier pages may hold any number of rows.
 */
export const getPageRowOffset = <TData extends RowData>(
    table: DataGridTable<TData>,
): number => {
    const { manualPagination, rowCount } = table.options;
    if (manualPagination && rowCount === undefined) {
        return 0;
    }
    const { pageIndex, pageSize } = table.atoms.pagination.get();
    return pageIndex * pageSize;
};

/** Assembles the TanStack options object for `useTable`. */
export const buildTableOptions = <TData extends RowData>(
    input: BuildTableOptionsInput<TData>,
): TableOptions<DataGridFeatures, TData> => {
    const { capabilities, isRowSelectable, serverRowCount, serverPageCount } =
        input;
    const search = getDataTableSearch(input.columns);
    return {
        features: dataGridFeatures,
        data: input.data,
        columns: input.columns,
        getRowId: input.getRowId,
        ...(input.getSubRows && { getSubRows: input.getSubRows }),
        ...(input.meta && { meta: input.meta }),
        state: input.controlledState,
        atoms: input.externalAtoms,
        initialState: input.initialState,
        ...input.changeHandlers,
        enableRowSelection: isRowSelectable
            ? (row) => isRowSelectable(row.original)
            : capabilities.hasRowSelection,
        enableSubRowSelection: capabilities.isSubRowSelectionCascading,
        enableSorting: !capabilities.hasSeparatorRows,
        manualSorting: capabilities.isServerSorting,
        sortDescFirst: false,
        enableGlobalFilter: capabilities.hasGlobalFilter,
        globalFilterFn: search.filter,
        getColumnCanGlobalFilter: (column) => column.id === search.primaryId,
        manualFiltering: capabilities.isServerFiltering,
        enableExpanding: capabilities.hasExpanding || capabilities.hasGrouping,
        autoResetExpanded: false,
        enableGrouping: capabilities.hasGrouping,
        manualPagination: !capabilities.hasClientPagination,
        autoResetPageIndex: false,
        ...(serverRowCount !== undefined && { rowCount: serverRowCount }),
        ...(serverPageCount !== undefined && { pageCount: serverPageCount }),
        enableCellSelection: true,
        enableCellRangeSelection: capabilities.hasRangeSelection,
        enableMultiCellRangeSelection: false,
        enableCellSelectionDrag: capabilities.hasRangeSelection,
        autoResetCellSelection: false,
        enableColumnResizing: true,
        columnResizeMode: "onChange",
    };
};
